import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Container,
  Paper,
  Tabs,
  Tab,
  Box,
  Typography,
  Alert,
  useMediaQuery,
  GlobalStyles,
  alpha,
} from '@mui/material';
import { createTheme, ThemeProvider, useTheme, keyframes, GlobalStyles as MuiGlobalStyles } from '@mui/material/styles';
import { styled } from '@mui/system';
import AuthForm from '../components/AuthForm';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import BusinessIcon from '@mui/icons-material/Business';
import GroupsIcon from '@mui/icons-material/Groups';
import mapBg from '../assests/map.jpg';
import SD_logo from '../assests/shipperdock.png';
import PlaystoreBadge from '../components/PlaystoreBadge'; // adjust path to wherever you saved it

const theme = createTheme({
  palette: {
    primary: { main: '#11224d' },
    secondary: { main: '#0e1d3a' },
    info: { main: '#4dabf5' },
    action: {
      disabledBackground: 'rgba(17, 34, 77, 0.5)',
      disabled: '#ffffff',
    },
  },
  typography: { fontFamily: 'Inter, Arial, sans-serif' },
  components: {
    MuiButton: { styleOverrides: { root: { borderRadius: 8, textTransform: 'none' } } },
    MuiPaper: { styleOverrides: { root: { borderRadius: 16 } } },
    MuiInputLabel: {
      styleOverrides: {
        shrink: {
          transform: 'translate(14px, -8px) scale(0.75) !important',
          backgroundColor: '#ffffff',
          padding: '0 4px',
          zIndex: 1,
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: {
          backgroundColor: '#4dabf5',
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          '&.Mui-selected': {
            color: '#4dabf5',
            backgroundColor: alpha('#4dabf5', 0.08),
          },

          minWidth: 0,
          minHeight: '36px',
          padding: '12px 16px',
          margin: '4px',
          borderRadius: '8px',
          transition: 'all 0.3s ease',
          backgroundColor: alpha('#11224d', 0.06),
          border: '1px solid rgba(17, 34, 77, 0.12)',
          boxShadow: '0 1px 3px rgba(0,0,0,0.08)',

          '&:hover': {
            backgroundColor: alpha('#4dabf5', 0.10),
            color: '#4dabf5',
            transform: 'translateY(-2px)',
            boxShadow: '0 4px 8px rgba(0,0,0,0.12)',
          },

          '@media (max-width: 600px)': {
            padding: '8px 8px',
            margin: '2px',
            minHeight: '32px',
            fontSize: '0.70rem',
            borderRadius: '6px',

            '&:hover': {
              transform: 'none',
              backgroundColor: alpha('#4dabf5', 0.10),
              color: '#4dabf5',
            },
          },

          '@media (min-width: 601px) and (max-width: 960px)': {
            padding: '10px 12px',
            margin: '3px',
            fontSize: '0.78rem',
          },
        },
      },
    },
  },
});

const brandColors = {
  primary: '#11224d',
  secondary: '#0e1d3a',
  warning: '#ffb300',
  white: '#ffffff',
  offWhite: '#f8fafd',
  textMain: '#11224d',
  textLight: '#5c6b85',
  footerBg: '#0b162a',
};

const blinkText = keyframes`
  50% { opacity: 0.3; }
`;

const truckDrive = keyframes`
  0%   { transform: translateY(-10px); }
  50%  { transform: translateY(10px);  }
  100% { transform: translateY(-10px); }
`;

const TruckWrap = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '12px',
  '& .animated-truck': {
    animation: `${truckDrive} 2.4s ease-in-out infinite`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
});

const RoleCard = styled(Box)(({ theme, selected }) => ({
  flex: 1,
  padding: theme.spacing(2),
  borderRadius: '12px',
  border: `2px solid ${selected ? '#4dabf5' : 'transparent'}`,
  backgroundColor: selected ? alpha('#4dabf5', 0.05) : '#ffffff',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  cursor: 'pointer',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  boxShadow: selected ? '0 8px 20px rgba(77, 171, 245, 0.2)' : '0 2px 4px rgba(0,0,0,0.05)',

  '&:hover': {
    backgroundColor: alpha('#11224d', 0.05),
    transform: 'translateY(-2px) scale(1.07)',
    color: '#4dabf5',
    '& .MuiSvgIcon-root': {
      color: '#4dabf5',
      fontSize: '1.4rem',
      transition: 'font-size 0.2s ease',
    },
  },

  '& .icon-box': {
    width: '50px',
    height: '50px',
    borderRadius: '10px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: theme.spacing(1),
    backgroundColor: selected ? '#4dabf5' : alpha('#11224d', 0.05),
    color: selected ? '#fff' : '#11224d',
    transition: 'all 0.3s ease',
  },
}));

const GradientPaper = styled(Paper)(({ theme }) => ({
  background: 'rgba(255, 255, 255, 0.9)',
  backdropFilter: 'blur(10px)',
  display: 'flex',
  overflow: 'hidden',
  boxShadow: '0 20px 40px rgba(0,0,0,0.4)',
  border: '1px solid rgba(255, 255, 255, 0.1)',
  width: '100%',
  maxWidth: '900px',
  boxSizing: 'border-box',
  borderRadius: '16px',

  '& .left-panel': {
    background: 'linear-gradient(180deg, #11224d 0%, #1a3a6d 100%)',
    padding: theme.spacing(4),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'white',
    width: '40%',
    boxSizing: 'border-box',
  },
  '& .right-panel': {
    background: '#f8fafd',
    padding: theme.spacing(4),
    width: '60%',
    display: 'flex',
    flexDirection: 'column',
    borderRadius: 0,
    boxSizing: 'border-box',
  },

  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    background: '#ffffff',
    backdropFilter: 'none',
    border: 'none',
    margin: theme.spacing(1),
    '& .left-panel': {
      width: '100%',
      padding: theme.spacing(4, 2),
      borderRadius: '16px 16px 0 0',
    },
    '& .right-panel': {
      width: '100%',
      padding: theme.spacing(4, 2),
      background: '#ffffff',
    },
  },
}));

const BackgroundImageLayer = styled('div', {
  shouldForwardProp: (prop) => prop !== 'loaded',
})(({ loaded }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundImage: `
    radial-gradient(circle at center, rgba(14, 29, 58, 0.4) 0%, rgba(14, 29, 58, 0.9) 100%),
    url(${mapBg})
  `,
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  backgroundAttachment: 'fixed',
  opacity: loaded ? 1 : 0,
  transition: 'opacity 1.5s ease-in-out',
  zIndex: 0,
}));

const AuthPageContainer = styled('div')(({ theme }) => ({
  minHeight: '100vh',
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: '#0e1d3a',
  position: 'relative',
  overflow: 'hidden',

  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundImage: `
      linear-gradient(rgba(77, 171, 245, 0.08) 1px, transparent 1px),
      linear-gradient(90deg, rgba(77, 171, 245, 0.08) 1px, transparent 1px)
    `,
    backgroundSize: '20px 20px',
    pointerEvents: 'none',
    zIndex: 1,
  },
}));

const ContentWrapper = styled('div')(({ theme }) => ({
  flexGrow: 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(2, 0),
  position: 'relative',
  zIndex: 2,
  width: '100%',
}));

function AuthPageInner({ onSignInSuccess }) {
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('sm'));

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);

  const [isSignUp, setIsSignUp] = useState(location.state?.isSignUp || false);
  const [role, setRole] = useState(location.state?.role || null);
  const [errorMessage, setErrorMessage] = useState(queryParams.get('error') ? 'Authentication failed. Please try again.' : null);
  const [roleError, setRoleError] = useState(false);
  const [bgLoaded, setBgLoaded] = useState(false);

  useEffect(() => {
    if (location.state) {
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  useEffect(() => {
    const img = new Image();
    img.src = mapBg;
    img.onload = () => setBgLoaded(true);
  }, []);

  const handleAuthSuccess = (signedInRole, userData) => {
    setErrorMessage(null);
    onSignInSuccess(signedInRole, userData);
  };

  return (
    <AuthPageContainer>
      <GlobalStyles
        styles={{
          '.right-panel form': { width: '100%' },
          '.right-panel .MuiTextField-root': {
            width: '100% !important',
            marginTop: '0 !important',
            marginBottom: '0 !important',
          },
          '.right-panel .MuiInputBase-root': { backgroundColor: '#ffffff' },
          '.right-panel .MuiFormControl-root': {
            width: '100% !important',
            marginTop: '0 !important',
            marginBottom: '0 !important',
          },
          '.right-panel input:-webkit-autofill, .right-panel input:-webkit-autofill:hover, .right-panel input:-webkit-autofill:focus, .right-panel input:-webkit-autofill:active':
            {
              WebkitBoxShadow: '0 0 0px 1000px #ffffff inset !important',
              boxShadow: '0 0 0px 1000px #ffffff inset !important',
              WebkitTextFillColor: '#11224d !important',
              caretColor: '#11224d',
              transition: 'background-color 9999s ease-in-out 0s',
            },
        }}
      />

      <BackgroundImageLayer loaded={bgLoaded} />

      <ContentWrapper>
        <Container
          component="main"
          maxWidth="md"
          disableGutters={isMobile}
          sx={{ display: 'flex', justifyContent: 'center' }}
        >
          <GradientPaper elevation={6}>
  <Box className="left-panel">
    {isMobile ? (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px' }}>
        <Box
          component="img"
          src={SD_logo}
          alt="ShipperDock"
          sx={{ width: '200px', height: 'auto', display: 'block', mx: 'auto' }}
        />
        
          <a href="https://www.shipperdock.com/"
          target="_blank"
          rel="noopener noreferrer"
          style={{
            textDecoration: 'none',
            background: '#FFB300',
            color: '#11224d',
            padding: '7px 16px',
            borderRadius: '8px',
            fontSize: '11px',
            fontWeight: 600,
            fontFamily: 'Arial, Helvetica, sans-serif',
            display: 'inline-block',
            letterSpacing: '0px',
            boxShadow: 'none',
            transition: 'all 0.2s ease',
          }}
          onMouseOver={(e) => { e.currentTarget.style.opacity = '0.9'; }}
          onMouseOut={(e) => { e.currentTarget.style.opacity = '1'; }}
        >
          Learn More About ShipperDock →
        </a>
        {/* badge removed from here on mobile */}
      </Box>
    ) : (
      <TruckWrap>
        <div className="animated-truck">
          <LocalShippingIcon style={{ fontSize: '150px', color: 'white' }} />
        </div>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>
          ShipperDock
        </Typography>
        <Box sx={{ mt: 2.5 }}>
          
            <a href="https://www.shipperdock.com/"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              textDecoration: 'none',
              background: '#FFB300',
              color: '#11224d',
              padding: '12px 28px',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: 600,
              fontFamily: 'Arial, Helvetica, sans-serif',
              display: 'inline-block',
              letterSpacing: '0px',
              boxShadow: 'none',
              transition: 'all 0.2s ease',
            }}
            onMouseOver={(e) => { e.currentTarget.style.opacity = '0.9'; }}
            onMouseOut={(e) => { e.currentTarget.style.opacity = '1'; }}
          >
            Learn More About ShipperDock →
          </a>
        </Box>
        <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
          <Typography
            variant="caption"
            sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.75rem', letterSpacing: '0.05em' }}
          >
            DOWNLOAD THE APP
          </Typography>
          <PlaystoreBadge />
        </Box>
      </TruckWrap>
    )}
  </Box>

  <Paper elevation={isMobile ? 0 : 3} className="right-panel">
    <Typography
      variant="h5"
      align="center"
      sx={{ mb: 0, fontWeight: 700, color: brandColors.primary }}
    >
      {isSignUp ? 'Sign Up' : 'Sign In'}
    </Typography>

    <Typography
      variant="body2"
      align="center"
      sx={{ mb: 1, color: brandColors.textLight }}
    >
      Please select your role to continue
    </Typography>

    {roleError && (
      <Typography variant="body1" color="error" sx={{ display: 'block', textAlign: 'center', mb: 0 }}>
        * Please select the type of your account
      </Typography>
    )}

    <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
      <Tabs
        value={role}
        onChange={(_, v) => { setRole(v); setRoleError(false); }}
        centered
        variant={isMobile ? 'fullWidth' : 'standard'}
      >
        <Tab label="Shipper" value="shipper" icon={<BusinessIcon />} iconPosition="start" />
        <Tab label="Carrier" value="carrier" icon={<LocalShippingIcon />} iconPosition="start" />
        <Tab label="Broker" value="broker" icon={<GroupsIcon />} iconPosition="start" />
      </Tabs>
    </Box>

    {errorMessage && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage}</Alert>}

    <Box sx={{ width: '100%' }}>
      <AuthForm
        isSignUp={isSignUp}
        role={role}
        onToggle={() => setIsSignUp(!isSignUp)}
        onSignInSuccess={handleAuthSuccess}
        onSignInError={setErrorMessage}
        onRoleError={() => setRoleError(true)}
      />
    </Box>

    {isMobile && (
      <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px' }}>
        <Typography
          variant="caption"
          sx={{ color: brandColors.textLight, fontSize: '0.68rem', letterSpacing: '0.05em' }}
        >
          DOWNLOAD THE APP
        </Typography>
        <Box sx={{ transform: 'scale(0.85)', transformOrigin: 'center' }}>
          <PlaystoreBadge />
        </Box>
      </Box>
    )}
  </Paper>
</GradientPaper>
        </Container>
      </ContentWrapper>
    </AuthPageContainer>
  );
}

export default function AuthPage({ onSignInSuccess }) {
  return (
    <ThemeProvider theme={theme}>
      <AuthPageInner onSignInSuccess={onSignInSuccess} />
    </ThemeProvider>
  );
}